import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTourismData } from '../../context/TourismContext';
// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper modules
import { Navigation, Autoplay, Pagination } from 'swiper/modules';

function OfferTwo() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { fetchServices, fetchFeaturedServices } = useTourismData();

    useEffect(() => {
        fetchFeaturedServicesData();
    }, []);

    const fetchFeaturedServicesData = async () => {
        try {
            setLoading(true);
            
            // Try to fetch featured services first
            let servicesData = [];
            try {
                const featuredData = await fetchFeaturedServices();
                if (featuredData && featuredData.results && featuredData.results.length > 0) {
                    servicesData = featuredData.results;
                } else if (featuredData && Array.isArray(featuredData) && featuredData.length > 0) {
                    servicesData = featuredData;
                }
            } catch (featuredError) {
                console.log('No featured services, trying all services');
            }
            
            // If no featured services, get regular services
            if (servicesData.length === 0) {
                const allServicesData = await fetchServices();
                if (allServicesData && allServicesData.results) {
                    servicesData = allServicesData.results;
                } else if (allServicesData && Array.isArray(allServicesData)) {
                    servicesData = allServicesData;
                }
            }

            // Limit to 8 services for display
            const limitedServices = servicesData.slice(0, 8);

            if (limitedServices.length > 0) {
                setServices(limitedServices);
            } else {
                // Fallback to demo data if API fails
                setServices([
                    {
                        id: 1,
                        name: "Professional Tour Guide",
                        main_image: null,
                        price: "50.00",
                        duration: "Full day",
                        service_type: "packages"
                    },
                    {
                        id: 2,
                        name: "Travel Insurance",
                        main_image: null,
                        price: "25.00",
                        duration: "Per trip",
                        service_type: "packages"
                    },
                    {
                        id: 3,
                        name: "Equipment Rental",
                        main_image: null,
                        price: "35.00",
                        duration: "Per day",
                        service_type: "packages"
                    },
                    {
                        id: 4,
                        name: "Transportation",
                        main_image: null,
                        price: "45.00",
                        duration: "Per trip",
                        service_type: "car_hire"
                    }
                ]);
            }
        } catch (error) {
            console.error('Error fetching featured services:', error);
            setError('Unable to load services');
            // Set fallback data
            setServices([
                {
                    id: 1,
                    name: "Professional Tour Guide",
                    main_image: null,
                    price: "50.00",
                    duration: "Full day",
                    service_type: "packages"
                },
                {
                    id: 2,
                    name: "Travel Insurance",
                    main_image: null,
                    price: "25.00",
                    duration: "Per trip",
                    service_type: "packages"
                },
                {
                    id: 3,
                    name: "Equipment Rental",
                    main_image: null,
                    price: "35.00",
                    duration: "Per day",
                    service_type: "packages"
                },
                {
                    id: 4,
                    name: "Transportation",
                    main_image: null,
                    price: "45.00",
                    duration: "Per trip",
                    service_type: "car_hire"
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) {
            return '/assets/img/destination/destination_4_1.jpg';
        }
        
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        
        if (imagePath.startsWith('/')) {
            return `https://tours.dzsoftech.net${imagePath}`;
        }
        
        return `https://tours.dzsoftech.net/media/${imagePath}`;
    };

    // Loading state
    if (loading) {
        return (
            <section className="position-relative overflow-hidden space-bottom" id="destination-sec">
                <div className="container">
                    <div className="title-area text-center">
                        <span className="sub-title">Services We Offer</span>
                        <h2 className="sec-title">Our Amazing Services</h2>
                    </div>
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading services...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading our amazing services...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="position-relative overflow-hidden space-bottom" id="destination-sec">
            <div className="container">
                {error && (
                    <div className="alert alert-warning text-center mb-4">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error} - Showing demo content
                    </div>
                )}
                
                <div className="title-area text-center">
                    <span className="sub-title">Services We Offer</span>
                    <h2 className="sec-title">Our Amazing Services</h2>
                </div>
                
                {/* Navigation Arrows */}
                <div className="slider-nav-wrap">
                    <div className="services-slider-prev slider-btn">
                        <i className="far fa-arrow-left"></i>
                    </div>
                    <div className="services-slider-next slider-btn">
                        <i className="far fa-arrow-right"></i>
                    </div>
                </div>
                
                {/* Services Slider */}
                <div className="services-slider-container">
                    {services.length > 0 ? (
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={4}
                            loop={services.length > 4}
                            autoplay={{
                                delay: 4000,
                                disableOnInteraction: false,
                            }}
                            navigation={{
                                nextEl: '.services-slider-next',
                                prevEl: '.services-slider-prev',
                            }}
                            pagination={{
                                clickable: true,
                                el: '.services-pagination'
                            }}
                            breakpoints={{
                                320: {
                                    slidesPerView: 1,
                                    spaceBetween: 16,
                                },
                                576: {
                                    slidesPerView: 1,
                                    spaceBetween: 20,
                                },
                                768: {
                                    slidesPerView: 2,
                                    spaceBetween: 20,
                                },
                                992: {
                                    slidesPerView: 3,
                                    spaceBetween: 24,
                                },
                                1200: {
                                    slidesPerView: 4,
                                    spaceBetween: 24,
                                }
                            }}
                            className="services-swiper"
                        >
                            {services.map((service, index) => (
                                <SwiperSlide key={service.id}>
                                    <div className="destination-item th-ani">
                                        <div className="destination-item_img global-img">
                                            <img 
                                                src={getImageUrl(service.main_image)} 
                                                alt={service.name}
                                                onError={(e) => {
                                                    e.target.src = `/assets/img/destination/destination_4_${(index % 8) + 1}.jpg`;
                                                }}
                                            />
                                        </div>
                                        <div className="destination-content">
                                            <h3 className="box-title">
                                                <Link to={`/service/${service.id}`}>{service.name}</Link>
                                            </h3>
                                            <p className="destination-text">
                                                ${service.price} - {service.duration}
                                            </p>
                                            <div className="service-features">
                                                <span className="service-type badge bg-primary">
                                                    {service.service_type?.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <Link to="/contact" className="th-btn style4 th-icon">
                                                Book Now
                                            </Link>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="col-12 text-center py-5">
                            <div className="alert alert-info">
                                <h4>No Services Available</h4>
                                <p className="mb-0">Please check back later or contact us for more information.</p>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Pagination dots */}
                <div className="services-pagination swiper-pagination-style1 text-center mt-4"></div>
                
                {services.length > 0 && (
                    <div className="text-center mt-4">
                        <Link to="/service" className="th-btn style3">
                            View All Services
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

export default OfferTwo;
